import React, { useState } from 'react'
import { Button } from '~/components/button'
import { ButtonBack } from './components/button-back'
import { ButtonBarCode } from './components/button-barcode'
import { ButtonMicrophone } from './components/button-microphone'
import * as S from './search-bar.styles'
import type { ISearchBarProps } from './types'
import { mapButtonSize } from './utils'

const SearchBarRoot = (props: ISearchBarProps) => {
  const {
    value,
    size = 'medium',
    onChangeText,
    children,
    onClear,
    placeholder,
    maxLength,
    showInternalSearchIcon = true,
    onFocus,
    onBlur,
    ...rest
  } = props
  const [isFocused, setIsFocused] = useState(false)

  const componentProperties = {
    size,
    hasFocused: isFocused,
    hasFill: (value && !isFocused) || false,
    hasFilledAndActive: (value && isFocused) || false,
  }
  const textInputProperties = {
    hasFocused: isFocused,
    hasFilledAndActive: (value && isFocused) || false,
  }

  const renderInternalComponents = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === ButtonBarCode) {
      return <ButtonBarCode {...child.props} size={mapButtonSize(size)} />
    }
    if (child.type === ButtonMicrophone) {
      return <ButtonMicrophone {...child.props} size={mapButtonSize(size)} />
    }
  })

  const renderButtonBack = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === ButtonBack) {
      return <ButtonBack {...child.props} size={mapButtonSize(size)} />
    }
  })

  return (
    <S.Root>
      {renderButtonBack}
      <S.InputContainer {...componentProperties}>
        {showInternalSearchIcon && (
          <Button.Root size={mapButtonSize(size)} variant="neutral-tertiary">
            <Button.Icon symbol="rdicon-search" />
          </Button.Root>
        )}
        <S.StyledInput
          value={value}
          numberOfLines={props.numberOfLines || 1}
          multiline={props.multiline || false}
          onChangeText={onChangeText}
          onFocus={() => {
            setIsFocused(true)
            if (typeof onFocus === 'function') {
              onFocus()
            }
          }}
          onBlur={() => {
            setIsFocused(false)
            if (typeof onBlur === 'function') {
              onBlur()
            }
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          {...textInputProperties}
          {...rest}
        />
        {value ? (
          <Button.Root
            size={mapButtonSize(size)}
            variant="neutral-tertiary"
            onPress={onClear}
            testID="search-bar-clear-button"
          >
            <Button.Icon symbol="rdicon-dismiss-circle-outline" />
          </Button.Root>
        ) : null}
        {renderInternalComponents}
      </S.InputContainer>
    </S.Root>
  )
}

export const SearchBar = {
  Root: SearchBarRoot,
  ButtonBarCode,
  ButtonMicrophone,
  ButtonBack,
}

export type { ISearchBarProps }
